import { css, cx } from "@emotion/css";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Menu from "src/components/Default/LekUI/Menu";

import ViewWithPagination from "../NewComponents/Search/ViewWithPagination";

interface TableRowData {
	id: string | number;
	[key: string]: unknown;
}

interface MobileTableProps {
	list: TableRowData[];
	onSelectActions: () => Array<{ title: string; onClick: (event: React.MouseEvent) => void }>;
	page: number;
	setPage: (page: number) => void;
	selectedList: TableRowData[];
	addToSelectedList: (row: TableRowData) => void;
	count: number;
	clearSelectedRows: () => void;
	EmptyBox?: ReactNode;
	renderMobileRow?: (row: TableRowData & { disabled: boolean }, className: string) => ReactNode;
}

function MobileTable(props: MobileTableProps) {
	const { t } = useTranslation();
	const [isSelectedMode, setSelectedMode] = useState(false);

	const container = document.getElementById("content");
	const isEmptyList = !(props.list || []).length;

	const setMode = () => {
		props.clearSelectedRows();
		setSelectedMode(true);
	};

	const closeMode = () => {
		props.clearSelectedRows();
		setSelectedMode(false);
	};

	const onElementClick = (row: TableRowData) => {
		if (isSelectedMode) {
			return props.addToSelectedList(row);
		}
	};

	const renderList = () => {
		if (isEmptyList) {
			return props.EmptyBox;
		}

		return props.list.map((row) => {
			const isSelected = props.selectedList.some(({ id }) => id === row.id);
			return (
				<li key={row.id} onClick={isSelectedMode ? () => onElementClick(row) : undefined}>
					{props.renderMobileRow?.(
						{
							...row,
							disabled: isSelectedMode,
						},
						isSelected ? "border-medium-primary" : "",
					)}
				</li>
			);
		});
	};

	const renderCounterBox = () => {
		return (
			!isEmptyList &&
			isSelectedMode && (
				<div className="table--select-mode content-box">
					<span>
						{t("table.exam.selectedItems")}: {props.selectedList.length}
					</span>
					<Menu
						icon={<MoreVertIcon />}
						items={props.onSelectActions()}
						closeAfterClick
					/>
				</div>
			)
		);
	};

	const renderToggleSelectedMode = () => {
		return (
			!isEmptyList && (
				<span
					className={cx(
						"p-absolute link-button",
						css`
              top: -25px;
              right: 8px;
              font-size: 14px !important;
            `,
					)}
					onClick={() => (isSelectedMode ? closeMode() : setMode())}
				>
					{isSelectedMode ? t("commands.unmark") : t("commands.mark")}
				</span>
			)
		);
	};

	const scrollToTop = () => {
		return container?.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<ViewWithPagination
			count={props.count}
			scrollToTop={scrollToTop}
			page={props.page}
			setPage={(page: number) => props.setPage(page)}
		>
			{renderToggleSelectedMode()}
			<ul className="list-type-none p-0 flex flex-col gap-12">
				{renderCounterBox()}
				{renderList()}
			</ul>
		</ViewWithPagination>
	);
}

export default MobileTable;
